import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {useId, useState} from "react";

type Props = {
  /** Ligne visible en permanence — ≤ 60 caractères, tient sur une ligne à 375 px (#863). */
  short: string;
  /** Cause complète + qui peut agir, affiché seulement une fois déplié. */
  detail: string;
  className?: string;
};

/**
 * Divulgation progressive d'un message de champ (#863) : la ligne courte
 * reste seule visible par défaut, le détail passe derrière un `<button>`
 * natif — clavier (Enter/Espace) et tap gérés par le navigateur, pas de div
 * cliquable maison. `aria-expanded` porte l'état pour les lecteurs d'écran,
 * relié au détail par `aria-controls`. `role="status"` annonce l'apparition
 * du message lui-même (aria-live="polite" implicite) — pas seulement le
 * dépliage.
 *
 * `width: 100%` sur la racine : dans `FloatingUnitField`, ce composant est un
 * enfant direct d'un `InputGroup` reactstrap (`display: flex; flex-wrap:
 * wrap`) — sans cette largeur pleine, le message resterait sur la ligne du
 * champ au lieu de passer dessous, comme le fait `.invalid-feedback` de
 * Bootstrap qu'il remplace.
 */
export function ProgressiveDisclosureMessage({short, detail, className}: Props) {
  const [expanded, setExpanded] = useState(false);
  const detailId = useId();

  return (
    <div
      className={className}
      role="status"
      style={{width: "100%", fontSize: "12px", color: "#dc3545"}}
    >
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        /**
         * `AvForm` (legacy, `ShiftingFormPart`) intercepte tout Entrée qui
         * bubble jusqu'au formulaire pour déclencher sa propre soumission
         * (#1132, cassait le dépliant au clavier) — stopper la propagation
         * ici laisse l'activation native du bouton (clic sur Entrée) suivre
         * son cours sans jamais atteindre ce handler.
         */
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.stopPropagation();
          }
        }}
        aria-expanded={expanded}
        aria-controls={detailId}
        style={{
          background: "none",
          border: "none",
          padding: "6px 0",
          margin: 0,
          minHeight: "32px",
          color: "inherit",
          font: "inherit",
          textAlign: "left",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: "4px",
        }}
      >
        <span>{short}</span>
        <FontAwesomeIcon icon={expanded ? "chevron-up" : "chevron-down"} size="xs" />
      </button>
      {expanded && (
        <div id={detailId} style={{paddingBottom: "4px"}}>
          {detail}
        </div>
      )}
    </div>
  );
}
